import { queryOptions } from "@tanstack/react-query";
import type {
  AddBikeModelMaterialInput,
  BikeDto,
  BikeId,
  BikeModelDto,
  BikeModelId,
  BikePickerDto,
  CreateBikeInput,
  CreateBikeModelInput,
  MaterialDto,
  PaginatedResponse,
  PaginationParams,
  UpdateBikeInput,
  UpdateBikeModelInput,
} from "@yalana/contracts";
import { fetchApi } from "../../../shared/infrastructure/api-client";
import { bikeModelQueries } from "./bike-models.queries";
import { toListSearchParams } from "../../../shared/utils/list-search-params";

type BikeImportResult = { created: number; updated: number; errors: string[] };

export const bikeQueries = {
  all: () => ["bikes"] as const,
  list: (
    params: PaginationParams & {
      customerId?: string;
      locationId?: string | null;
      status?: string;
      q?: string;
      sort?: string;
      dir?: "asc" | "desc";
    } = {
      page: 1,
      pageSize: 10,
    },
  ) =>
    queryOptions({
      queryKey: [...bikeQueries.all(), "list", params] as const,
      queryFn: () =>
        fetchApi<PaginatedResponse<BikeDto>>("/api/bikes", {
          searchParams: toListSearchParams({
            page: params.page,
            pageSize: params.pageSize,
            customerId: params.customerId,
            locationId: params.locationId,
            status: params.status,
            q: params.q,
            sort: params.sort,
            dir: params.dir,
          }),
        }),
    }),
  listAll: () =>
    queryOptions({
      queryKey: [...bikeQueries.all(), "list", "all"] as const,
      queryFn: async () => {
        const res = await fetchApi<PaginatedResponse<BikeDto>>("/api/bikes", {
          searchParams: { page: "1", pageSize: "500" },
        });
        return res.data;
      },
    }),
  // picker: minimal-fields listing gated by requests.write (not admin.bikes.read) —
  // for request-builder selects (RequestEditor, welcome dashboard stats). Never use
  // this for admin bike management screens — those need the full BikeDto via listAll().
  picker: (params: { customerId?: string; locationId?: string } = {}) =>
    queryOptions({
      queryKey: [...bikeQueries.all(), "picker", params] as const,
      queryFn: () => {
        const searchParams: Record<string, string> = {};
        if (params.customerId) searchParams.customerId = params.customerId;
        if (params.locationId) searchParams.locationId = params.locationId;
        return fetchApi<BikePickerDto[]>("/api/bikes/picker", {
          searchParams,
        });
      },
    }),
  models: (
    params: PaginationParams & {
      customerId?: string;
      q?: string;
      sort?: string;
      dir?: "asc" | "desc";
    } = { page: 1, pageSize: 10 },
  ) =>
    queryOptions({
      queryKey: [...bikeQueries.all(), "models", "list", params] as const,
      queryFn: () =>
        fetchApi<PaginatedResponse<BikeModelDto>>("/api/bikes/models", {
          searchParams: toListSearchParams({
            page: params.page,
            pageSize: params.pageSize,
            customerId: params.customerId,
            q: params.q,
            sort: params.sort,
            dir: params.dir,
          }),
        }),
    }),
  modelsAll: bikeModelQueries.all,
  detail: (id: BikeId) =>
    queryOptions({
      queryKey: [...bikeQueries.all(), id] as const,
      queryFn: () => fetchApi<BikeDto>(`/api/bikes/${id}`),
    }),
  modelDetail: (id: BikeModelId) =>
    queryOptions({
      queryKey: [...bikeQueries.all(), "models", id] as const,
      queryFn: () => fetchApi<BikeModelDto>(`/api/bikes/models/${id}`),
    }),
  modelMaterials: (modelId: BikeModelId) =>
    queryOptions({
      queryKey: [...bikeQueries.all(), "models", modelId, "materials"] as const,
      queryFn: () =>
        fetchApi<MaterialDto[]>(`/api/bikes/models/${modelId}/materials`),
    }),
  materialModels: (materialId: string) =>
    queryOptions({
      queryKey: [
        ...bikeQueries.all(),
        "models",
        "by-material",
        materialId,
      ] as const,
      queryFn: () =>
        fetchApi<BikeModelDto[]>(
          `/api/materials/catalog/${materialId}/bike-models`,
        ),
    }),
};

export const bikeMutations = {
  create: () => ({
    mutationFn: (input: CreateBikeInput) =>
      fetchApi<BikeDto>("/api/bikes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      }),
  }),
  update: (id: BikeId) => ({
    mutationFn: (input: UpdateBikeInput) =>
      fetchApi<BikeDto>(`/api/bikes/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      }),
  }),
  delete: (id: BikeId) => ({
    mutationFn: () => fetchApi(`/api/bikes/${id}`, { method: "DELETE" }),
  }),
  deactivate: (id: BikeId) => ({
    mutationFn: () =>
      fetchApi(`/api/bikes/${id}/deactivate`, { method: "PATCH" }),
  }),
  reactivate: (id: BikeId) => ({
    mutationFn: () =>
      fetchApi(`/api/bikes/${id}/reactivate`, { method: "PATCH" }),
  }),
  createModel: () => ({
    mutationFn: (input: CreateBikeModelInput) =>
      fetchApi<BikeModelDto>("/api/bikes/models", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      }),
  }),
  updateModel: (id: BikeModelId) => ({
    mutationFn: (input: UpdateBikeModelInput) =>
      fetchApi<BikeModelDto>(`/api/bikes/models/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      }),
  }),
  deleteModel: (id: BikeModelId) => ({
    mutationFn: () => fetchApi(`/api/bikes/models/${id}`, { method: "DELETE" }),
  }),
  deactivateModel: (id: BikeModelId) => ({
    mutationFn: () =>
      fetchApi(`/api/bikes/models/${id}/deactivate`, { method: "PATCH" }),
  }),
  reactivateModel: (id: BikeModelId) => ({
    mutationFn: () =>
      fetchApi(`/api/bikes/models/${id}/reactivate`, { method: "PATCH" }),
  }),
  addModelMaterial: (modelId: BikeModelId) => ({
    mutationFn: (input: AddBikeModelMaterialInput) =>
      fetchApi<void>(`/api/bikes/models/${modelId}/materials`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      }),
  }),
  removeModelMaterial: (modelId: BikeModelId) => ({
    mutationFn: (materialId: string) =>
      fetchApi<void>(`/api/bikes/models/${modelId}/materials/${materialId}`, {
        method: "DELETE",
      }),
  }),
  import: () => ({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      return fetchApi<BikeImportResult>("/api/bikes/import", {
        method: "POST",
        body: formData,
      });
    },
  }),
};
